import { useState, useCallback, useMemo } from 'react';

const usePaymentCalculations = (tarifs, fraisInscription, tranchesPaiement) => {
  // États du paiement
  const [montantScolarite, setMontantScolarite] = useState(0);
  const [montantVerse, setMontantVerse] = useState(0);
  const [montantPremiereTranche, setMontantPremiereTranche] = useState(0);
  const [paiementComplet, setPaiementComplet] = useState(false);
  const [paiementFraisInscription, setPaiementFraisInscription] = useState(false);
  const [remiseType, setRemiseType] = useState('pourcentage');
  const [remiseValue, setRemiseValue] = useState(0);
  const [tarifsSup, setTarifsSup] = useState([]);
  const [optionsPaiement, setOptionsPaiement] = useState({});
  const [tranchesRestantes, setTranchesRestantes] = useState({});
  const [premiereTrancheModalite, setPremiereTrancheModalite] = useState(null);

  // Calcul de la remise
  const remiseCalculee = useMemo(() => {
    if (!tarifs?.prix) return 0;
    
    if (remiseType === 'pourcentage') {
      return (tarifs.prix * remiseValue) / 100;
    } else {
      return remiseValue;
    }
  }, [tarifs?.prix, remiseType, remiseValue]);

  // Calcul du total des tarifs supplémentaires
  const calculerTotalTarifsSup = useCallback((liste = tarifsSup) => {
    return liste.reduce((total, tarif) => total + tarif.prix, 0);
  }, [tarifsSup]);

  // Calcul du montant total
  const montantTotal = useMemo(() => {
    const baseScolarite = tarifs?.prix || 0;
    const frais = paiementFraisInscription ? fraisInscription : 0;
    const optionsTotal = calculerTotalTarifsSup();
    
    return baseScolarite + frais + optionsTotal - remiseCalculee;
  }, [tarifs?.prix, paiementFraisInscription, fraisInscription, calculerTotalTarifsSup, remiseCalculee]);

  // Calcul du montant actuel à payer
  const calculerMontantActuel = useCallback(() => {
    const montantSaisi = Number(montantVerse) || 0;
    const fraisInscriptionTotal = paiementFraisInscription ? fraisInscription : 0;
    const optionsTotal = tarifsSup
      .filter(t => optionsPaiement[t.id])
      .reduce((sum, t) => sum + t.prix, 0);
    
    return montantSaisi + fraisInscriptionTotal + optionsTotal;
  }, [montantVerse, paiementFraisInscription, fraisInscription, tarifsSup, optionsPaiement]);

  // Calcul de la répartition des paiements par tranche
  const calculerRepartitionPaiement = useCallback((montantTotal) => {
    const montant = Number(montantTotal) || 0;
    let montantRestant = montant;
    const repartition = {};

    // Parcourir les tranches dans l'ordre
    tranchesPaiement.forEach(tranche => {
      const montantTranche = (tarifs?.prix * tranche.pourcentage) / 100;
      
      if (montantRestant > 0) {
        // Calculer combien on peut payer sur cette tranche
        const montantPaye = Math.min(montantRestant, montantTranche);
        repartition[tranche.id] = {
          montantPaye,
          montantRestant: montantTranche - montantPaye,
          estPayeComplet: montantPaye >= montantTranche,
          estPayePartiel: montantPaye > 0 && montantPaye < montantTranche
        };
        montantRestant -= montantPaye;
      } else {
        repartition[tranche.id] = {
          montantPaye: 0,
          montantRestant: montantTranche,
          estPayeComplet: false,
          estPayePartiel: false
        };
      }
    });

    return repartition;
  }, [tranchesPaiement, tarifs?.prix]);

  // Génération du libellé de versement
  const getLibelleVersement = useCallback((tranche, montant) => {
    const formatter = new Intl.NumberFormat('fr-FR');
    return `Versement de ${formatter.format(montant)} FCFA - ${tranche.pourcentage}% des frais de scolarité (${tranche.libelle})`;
  }, []);

  // Gestion des changements de montant versé
  const handleMontantVerseChange = useCallback((e) => {
    const montant = e.target.value;
    setMontantVerse(montant);
    const repartition = calculerRepartitionPaiement(montant);
    setTranchesRestantes(
      Object.keys(repartition).reduce((acc, trancheId) => ({
        ...acc,
        [trancheId]: repartition[trancheId].montantRestant
      }), {})
    );
  }, [calculerRepartitionPaiement]);

  // Gestion des remises
  const handleRemiseChange = useCallback((e) => {
    const value = parseFloat(e.target.value) || 0;
    setRemiseValue(value);
  }, []);

  // Toggle des tarifs supplémentaires
  const toggleTarifSup = useCallback((tarif) => {
    const existe = tarifsSup.some(t => t.id === tarif.id);
    let newTarifsSup = existe ? 
      tarifsSup.filter(t => t.id !== tarif.id) : 
      [...tarifsSup, tarif];
    
    setTarifsSup(newTarifsSup);
    
    // Mettre à jour le statut de paiement de l'option
    setOptionsPaiement(prev => ({
      ...prev,
      [tarif.id]: existe ? undefined : false
    }));
  }, [tarifsSup]);

  return {
    // États
    montantScolarite,
    setMontantScolarite,
    montantVerse,
    setMontantVerse,
    montantPremiereTranche,
    setMontantPremiereTranche,
    paiementComplet,
    setPaiementComplet,
    paiementFraisInscription,
    setPaiementFraisInscription,
    remiseType,
    setRemiseType,
    remiseValue,
    setRemiseValue,
    tarifsSup,
    setTarifsSup,
    optionsPaiement,
    setOptionsPaiement,
    tranchesRestantes,
    setTranchesRestantes,
    premiereTrancheModalite,
    setPremiereTrancheModalite,

    // Calculs
    remiseCalculee,
    montantTotal,
    calculerMontantActuel,
    calculerRepartitionPaiement,

    // Actions
    handleMontantVerseChange,
    handleRemiseChange,
    toggleTarifSup,
    getLibelleVersement,
    calculerTotalTarifsSup
  };
};

export default usePaymentCalculations; 